import React, { useState, useEffect, useContext, useRef, useCallback } from "react"
import I18n from "@helper/i18nInstance"
import _ from "lodash"
import scrollIntoView from "scroll-into-view-if-needed"
import onEnterOrSpace from "@helper/onEnterOrSpace"

import "./Result.scss"
import Calculating from "./Calculating"
import ExtraContent from "./ExtraContent"
import usePianoId from "./use_pianoid"

import { MountIdContext, QuizActions, useQuiz } from "@Quiz/context"
import { useQuizCard } from "@Quiz/Ui/QuizCard"
import ContainedImg from "@Quiz/Ui/ContainedImg"
import Button from "@ui/Button"
import Link from "@ui/Link"
import { Icon } from "@ui/Icon"
import { RANKING_HOLDER_CLASS } from "@Quiz/Ranking"
import { isRankingRelevant } from "@Quiz/helper"
import { WithErrorPopup } from "./ErrorPopup"
import QuizEmbedInfoContext from "@Quiz/QuizEmbedInfoContext"

const RawResult: React.FC<{ quiz: Quiz.Api.Quiz, quizResult: Quiz.Api.QuizResult, skipCalculatingAnimation?: boolean }> = ({ quiz, quizResult, skipCalculatingAnimation }) => {
  const { recalcInnerHeight } = useQuizCard()

  const enteredHighscore = _.isNumber(quizResult.normal_quiz?.own_rank)
  const [showEvalAni, setShowEvalAni] = useState(!enteredHighscore && !skipCalculatingAnimation)

  useEffect(() => {
    recalcInnerHeight()
  }, [enteredHighscore, showEvalAni, recalcInnerHeight])

  const hasExtra = quizResult.extra_content && quizResult.extra_content.length > 0

  const rankRelevant = isRankingRelevant(quiz)

  const optionalExtraContent = <>
    {hasExtra &&
      <ExtraContent type={quizResult.extra_content as Quiz.Api.ExtraContent} />}
    {quiz.advertised_quizzes && quiz.advertised_quizzes.length > 0 && _.isNumber(quizResult?.normal_quiz?.own_rank) &&
      <AdvertisedQuizzes quizzes={quiz.advertised_quizzes} />}
  </>

  return (
    <WithErrorPopup>
      <div className="neo__quizResult" data-testid="quiz-result">
        {showEvalAni
          ? <Calculating done={() => setShowEvalAni(false)} />
          : (enteredHighscore
            ? (rankRelevant
                ? <EnteredHighscoreWhenRankIsRelevant>{optionalExtraContent}</EnteredHighscoreWhenRankIsRelevant>
                : <EnteredHighscoreWhenRankIsNotRelevant>{optionalExtraContent}</EnteredHighscoreWhenRankIsNotRelevant>)
            : <>
                {quizResult.normal_quiz &&
                  (rankRelevant
                    ? <NormalQuizWhenRankIsRelevant>{optionalExtraContent}</NormalQuizWhenRankIsRelevant>
                    : <NormalQuizWhenRankIsNotRelevant>{optionalExtraContent}</NormalQuizWhenRankIsNotRelevant>)}
                {quizResult.personality_test_points && <PersonalityTestPoints>{optionalExtraContent}</PersonalityTestPoints>}
                {quizResult.personality_test_multi && <PersonalityTestMulti>{optionalExtraContent}</PersonalityTestMulti>}
              </>)}

      </div>
    </WithErrorPopup>
  )
}

const Result: React.FC<{ skipCalculatingAnimation?: boolean }> = ({ skipCalculatingAnimation }) => {
  const { quiz } = useQuiz()
  if (quiz && quiz.result) {
    return <RawResult quiz={quiz} quizResult={quiz.result} skipCalculatingAnimation={skipCalculatingAnimation} />
  } else {
    return null
  }
}

const AdvertisedQuizzes: React.FC<{quizzes: {title: string, url: string}[]}> = ({ quizzes }) => {
  return <div className="neo__advertisedQuizzes">
    <h2>{I18n.t("advertised_quizzes.title")}</h2>
    <div className="neo__advertisedQuizzes__list">
      {_.map(quizzes, (q, index) => {
        return <div key={index}><Link href={q.url}>{q.title}</Link></div>
      })}
    </div>
  </div>
}

const EnteredHighscoreWhenRankIsRelevant: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { resultIcons } = useContext(QuizEmbedInfoContext)
  const { quiz, actions } = useQuiz()
  const { mountId } = useContext(MountIdContext)
  if (!quiz?.result) { return null }
  const ownRank = quiz?.result?.normal_quiz?.own_rank
  if (!_.isNumber(ownRank)) { return null }

  const scrollToRanking = () => {
    const topRankingHolderEl = document.getElementsByClassName(`${RANKING_HOLDER_CLASS}--${mountId ?? ""}`)[0]
    if (topRankingHolderEl) {
      scrollIntoView(
        topRankingHolderEl,
        {
          behavior: "smooth",
          scrollMode: "always"
        }
      )
    } else if (quiz.meta.ranking_url) {
      window.location.href = quiz.meta.ranking_url
    }
  }

  return <div className="neo__quizEnteredHighscore">
    <Icon iconIds={resultIcons.check} />
    <p className="neo__quizEnteredHighscore__iconSubtitle">{I18n.t("quiz.quiz_eval.entered_highscore.title")}</p>
    {/* Own rank  */}
    <h2 className="neo__quizEnteredHighscore__title">
      {I18n.t("quiz.quiz_eval.entered_highscore.congrats")}
      <br />
      {I18n.t("quiz.quiz_eval.entered_highscore.your_rank", { rank: ownRank })}
    </h2>
    {/* ? extra content   */}{optionalExtraContent}
    {/* ? link to ranking */}{isSet(quiz.meta.ranking_url) &&
      <Button onClick={scrollToRanking}>
        <span className="neo__quizResult__rankingButtonTextWithArrowPointingDown">{I18n.t("quiz.meta.show_ranking")}</span>
      </Button>}
    {/* Restart quiz      */}<Link onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.actions.cancel")}</Link>
  </div>
}

const EnteredHighscoreWhenRankIsNotRelevant: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { resultIcons } = useContext(QuizEmbedInfoContext)
  const { quiz, actions } = useQuiz()
  if (!quiz?.result) { return null }

  return <div className="neo__quizEnteredHighscore">
    <Icon iconIds={resultIcons.check} />
    <p className="neo__quizEnteredHighscore__iconSubtitle">{I18n.t("quiz.quiz_eval.entered_highscore.title_participate_in_raffle")}</p>
    {/* Good Luck! */}
    <h2 className="neo__quizEnteredHighscore__title">
      {I18n.t("quiz.quiz_eval.entered_highscore.good_luck")}
    </h2>
    {/* ? extra content   */}{optionalExtraContent}
    {/* Restart quiz      */}<Link onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.actions.cancel")}</Link>
  </div>
}

const PersonalityTestPoints: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { quiz, actions } = useQuiz()
  const perso = quiz?.result?.personality_test_points
  if (!perso) { return null }

  return <>
    <h3>{I18n.t("quiz.quiz_eval.personality_test.score_title")}</h3>
    <h2>{I18n.t("quiz.quiz_eval.personality_test.points", { points: perso.points })}</h2>
    {perso.image &&
      <div className="neo__quizResult__imgWithCreditsHolder">
        <ContainedImg className="neo__quizResult__img" src={perso.image["1x"]} />
        {perso.image_credit && <div className="neo__quizResult__imgCredit">{perso.image_credit}</div>}
      </div>}
    {perso.result_text && perso.result_text.length > 0 &&
      <div
        className="neo__quizResult__perso__resultText"
        dangerouslySetInnerHTML={{__html: perso.result_text}} />}
    <div className="neo__quizResult__perso__testName">{I18n.t("quiz.quiz_eval.personality_test.test_name", { name: quiz?.meta.title })}</div>
    {optionalExtraContent}
    <div className="neo__quizResult__buttons">
      <Button variant="secondary" onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.quiz_eval.personality_test.restart")}</Button>
    </div>
  </>
}

const PersonalityTestMulti: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { quiz, actions } = useQuiz()
  const perso = quiz?.result?.personality_test_multi
  if (!perso) { return null }

  return <>
    <h3>{I18n.t("quiz.quiz_eval.personality_test.multi_title")}</h3>
    <h2>{perso.result_title}<br />{perso.result_table[0].points}</h2>
    {perso.image &&
      <div className="neo__quizResult__imgWithCreditsHolder">
        <ContainedImg className="neo__quizResult__img" src={perso.image["1x"]} />
        {perso.image_credit && <div className="neo__quizResult__imgCredit">{perso.image_credit}</div>}
      </div>}
    {perso.result_text && perso.result_text.length > 0 &&
      <div
        className="neo__quizResult__perso__resultText"
        dangerouslySetInnerHTML={{__html: perso.result_text}} />}
    <MultiResultTableInsideQuizCard result_table={perso.result_table} />
    <div className="neo__quizResult__perso__testName">{I18n.t("quiz.quiz_eval.personality_test.test_name", { name: quiz?.meta.title })}</div>
    {optionalExtraContent}
    <div className="neo__quizResult__buttons">
      <Button variant="secondary" onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.quiz_eval.personality_test.restart")}</Button>
    </div>
  </>
}


const MultiResultTableInsideQuizCard: React.FC<{result_table: Quiz.Api.ResultTable}> = ({result_table}) => {
  return (
    <div className="neo__multiResultTableInsideCard">
      <h3>{I18n.t("quiz.quiz_eval.personality_test.result_table_title")}</h3>
      <table className="neo__multiResultTableInsideCard__table">
        <tbody>
          {_.map(result_table, (p, index) => {
            return <tr key={index}>
              <td>{p.title}</td>
              <td>{p.points}</td>
            </tr>
          })}
        </tbody>
      </table>
    </div>
  )
}

const isSet = (val: string | undefined) => val && val.length > 0

const RawNormalQuizWhenRankIsRelevant: React.FC<React.PropsWithChildren<{ quiz: Quiz.Api.Quiz, actions: QuizActions, normalResult: Quiz.Api.NormalQuizResult}>> = ({ quiz, actions, normalResult, children: optionalExtraContent }) => {
  const [ optinChecked, setOptinChecked ] = useState(!normalResult.optin_text || false)
  const canEnterHighscore = optinChecked
  const enterRankingCooldownRef = useRef(false)

  const reachedPointsText = _.isNumber(quiz?.meta.possible_points)
    ? I18n.t("quiz.quiz_eval.normal_quiz.reached_points", {points: quiz?.meta.points, total: quiz?.meta.possible_points})
    : I18n.t("quiz.quiz_eval.normal_quiz.reached_points_without_total", {points: quiz?.meta.points})

  const actuallyTryToEnterRanking = usePianoId(actions.enterRanking)
  const actuallyTryToEnterRankingRef = useRef(actuallyTryToEnterRanking)
  actuallyTryToEnterRankingRef.current = actuallyTryToEnterRanking

  const onClick = useCallback(() => {
    if (!enterRankingCooldownRef.current) {
      enterRankingCooldownRef.current = true
      actuallyTryToEnterRankingRef.current()
      window.setTimeout(() => {
        enterRankingCooldownRef.current = false
      }, 800)
    }
  }, [])

  return <>
    <h3>{I18n.t(`quiz.quiz_eval.normal_quiz.title_${normalResult.grade}`)}</h3>
    <h2>{reachedPointsText}</h2>
    {normalResult.image &&
      <div className="neo__quizResult__imgWithCreditsHolder">
        <ContainedImg className="neo__quizResult__img" src={normalResult.image["1x"]} />
        {normalResult.image_credit && <div className="neo__quizResult__imgCredit">{normalResult.image_credit}</div>}
      </div>}
    {/* ? Time needed   */}{isSet(normalResult.needed_minutes) && <div className="neo__quizResult__neededMinutes">{I18n.t("quiz.quiz_eval.normal_quiz.needed_minutes", {minutes: normalResult.needed_minutes})}</div>}
    {/* ? Result text   */}{isSet(normalResult.result_text) && <div className="neo__quizResult__text" dangerouslySetInnerHTML={{__html: normalResult.result_text || ""}} />}
    {optionalExtraContent}
    {/* ? Optin         */}{isSet(normalResult.optin_text) && <Optin checked={optinChecked} htmlText={normalResult.optin_text || ""} setChecked={setOptinChecked}/>}
    <p>{I18n.t("quiz.quiz_eval.normal_quiz.ranking_name_hint")}</p>
    <div className="neo__quizResult__buttons">
      {normalResult.enter_ranking_error && <EnterRankingError msg={normalResult.enter_ranking_error} />}
      <Button onClick={canEnterHighscore ? onClick : undefined}>{I18n.t("quiz.quiz_eval.normal_quiz.btn_enter_highscore")}</Button>
      <Button variant="secondary" onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.actions.cancel")}</Button>
    </div>
  </>
}

const NormalQuizWhenRankIsRelevant: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { quiz, actions } = useQuiz()
  if (!quiz?.result) { return null }
  const normalQuiz = quiz?.result?.normal_quiz
  if (!normalQuiz) { return null }

  return <RawNormalQuizWhenRankIsRelevant quiz={quiz} actions={actions} normalResult={normalQuiz}>{optionalExtraContent}</RawNormalQuizWhenRankIsRelevant>
}

const EnterRankingError: React.FC<{msg: string}> = ({ msg }) => <div className="neo__quizResult__enterRankingError">{msg}</div>

const RawNormalQuizWhenRankIsNotRelevant: React.FC<React.PropsWithChildren<{ quiz: Quiz.Api.Quiz, actions: QuizActions, normalResult: Quiz.Api.NormalQuizResult}>> = ({ actions, normalResult, children: optionalExtraContent }) => {
  const [ optinChecked, setOptinChecked ] = useState(!normalResult.optin_text || false)
  const canEnterHighscore = optinChecked

  // const reachedPointsText = _.isNumber(quiz?.meta.possible_points)
  //   ? I18n.t("quiz.quiz_eval.normal_quiz.reached_points", {points: quiz?.meta.points, total: quiz?.meta.possible_points})
  //   : I18n.t("quiz.quiz_eval.normal_quiz.reached_points_without_total", {points: quiz?.meta.points})

  const onClick = usePianoId(actions.enterRanking)

  return <>
    <h2>{I18n.t(`quiz.quiz_eval.normal_quiz.title_${normalResult.grade}`)}</h2>
    {normalResult.image &&
      <div className="neo__quizResult__imgWithCreditsHolder">
        <ContainedImg className="neo__quizResult__img" src={normalResult.image["1x"]} />
        {normalResult.image_credit && <div className="neo__quizResult__imgCredit">{normalResult.image_credit}</div>}
      </div>}
    {/* ? Time needed   */}{isSet(normalResult.needed_minutes) && <div className="neo__quizResult__neededMinutes">{I18n.t("quiz.quiz_eval.normal_quiz.needed_minutes", {minutes: normalResult.needed_minutes})}</div>}
    {/* ? Result text   */}{isSet(normalResult.result_text) && <div className="neo__quizResult__text" dangerouslySetInnerHTML={{__html: normalResult.result_text || ""}} />}
    {optionalExtraContent}
    {/* ? Optin         */}{isSet(normalResult.optin_text) && <Optin checked={optinChecked} htmlText={normalResult.optin_text || ""} setChecked={setOptinChecked}/>}
    <div className="neo__quizResult__buttons">
      {normalResult.enter_ranking_error && <EnterRankingError msg={normalResult.enter_ranking_error} />}
      <Button onClick={canEnterHighscore ? onClick : undefined}>{I18n.t("quiz.quiz_eval.normal_quiz.raffle.btn_participate")}</Button>
      <Button variant="secondary" onClick={() => actions.cancelQuiz()}>{I18n.t("quiz.actions.cancel")}</Button>
    </div>
  </>
}

const NormalQuizWhenRankIsNotRelevant: React.FC<React.PropsWithChildren> = ({children: optionalExtraContent}) => {
  const { quiz, actions } = useQuiz()
  if (!quiz?.result) { return null }
  const normalQuiz = quiz?.result?.normal_quiz
  if (!normalQuiz) { return null }

  return <RawNormalQuizWhenRankIsNotRelevant quiz={quiz} actions={actions} normalResult={normalQuiz}>{optionalExtraContent}</RawNormalQuizWhenRankIsNotRelevant>
}

const Optin: React.FC<{htmlText: string, checked: boolean, setChecked(checked: boolean): void}> = ({htmlText, checked, setChecked}) => {
  const onClick = () => setChecked(!checked)
  return <div className="neo__quizResult__optin">
    <div
      className={`neo__quizResult__optin__cb neo__quizResult__optin__cb--${checked ? "checked" : "notChecked"}`}
      role="checkbox"
      aria-checked={checked}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onEnterOrSpace(onClick)}/>
    <div className="neo__quizResult__optin__text" dangerouslySetInnerHTML={{__html: htmlText}} />
  </div>
}

export default Result
